import { Button, Eyebrow, Field, FixedFooter, Heading, PageContainer, SelectableCard, SelectableCardGroup, Text, TextArea } from "@/components/ui";
import { showToast } from "@/components/feedback"
import {
  MIN_CANCELLATION_REASON_LEN,
  canRequestCancellation,
  getCurrentStatus,
} from "@/lib/order-status"
import { requestOrderCancellation } from "@/lib/request-cancellation"
import { OrderResponse } from "@/types/external"
import { apiCustomer } from "@/api"
import { getMerchantId } from "@/lib/company-storage"
import { useMerchantCustomerConfig } from "@/hooks/use-merchant-customer-config"
import { usePageBackground } from "@/hooks/use-page-background"
import { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { OrderPageLoading } from "@/components/loaders"

const OPTION_CANCEL = "quero_cancelar"

/**
 * Full-screen “estou com um problema” flow.
 * Today a single option: “quero cancelar o meu pedido” + free-text reason.
 */
export function OrderProblemPage() {
  const { id: orderId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [order, setOrder] = useState<OrderResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedOption, setSelectedOption] = useState<string | null>(null)
  const [reason, setReason] = useState("")
  const [submitting, setSubmitting] = useState(false)
  usePageBackground()

  useEffect(() => {
    if (!orderId) {
      navigate("/profile/orders", { replace: true })
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const { data } = await apiCustomer(`/orders/${orderId}`)
        if (cancelled) return
        setOrder(data)
        if (!canRequestCancellation(data)) {
          showToast.error("Não é possível solicitar cancelamento neste pedido.")
          navigate(`/profile/orders/${orderId}`, { replace: true })
        }
      } catch {
        if (!cancelled) {
          showToast.error("Não foi possível carregar o pedido.")
          navigate("/profile/orders", { replace: true })
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [orderId, navigate])

  const orderMerchantId = order?.OrderMerchants?.[0]?.MerchantId ?? null
  const merchantId = orderMerchantId ?? getMerchantId()
  const { cancellationRequestEnabled, loading: configLoading } =
    useMerchantCustomerConfig(merchantId)

  useEffect(() => {
    if (!order || configLoading || cancellationRequestEnabled) return
    showToast.error("Não é possível solicitar cancelamento neste pedido.")
    navigate(`/profile/orders/${orderId}`, { replace: true })
  }, [order, configLoading, cancellationRequestEnabled, orderId, navigate])

  const reasonOk = useMemo(
    () => reason.trim().length >= MIN_CANCELLATION_REASON_LEN,
    [reason],
  )
  const canSubmit =
    selectedOption === OPTION_CANCEL && reasonOk && !submitting && !!orderId

  async function handleSubmit() {
    if (!canSubmit || !orderId) return
    setSubmitting(true)
    try {
      await requestOrderCancellation(orderId, reason)
      showToast.success("Solicitação de cancelamento enviada.")
      navigate(`/profile/orders/${orderId}`, { replace: true })
    } catch (err: unknown) {
      console.error(err)
      const msg =
        (err as { response?: { data?: { error?: string; message?: string } } })
          ?.response?.data?.message ||
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error ||
        "Não foi possível solicitar o cancelamento. Tente novamente."
      showToast.error(
        typeof msg === "string"
          ? msg
          : "Não foi possível solicitar o cancelamento.",
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (loading || !order || configLoading || !cancellationRequestEnabled) {
    return <OrderPageLoading />
  }

  const status = getCurrentStatus(order)

  return (
    <>
      <PageContainer top="header-gap" bottom="chrome" className="flex flex-col">
        <Heading level={1} size="page">
          Estou com um problema
        </Heading>
        <Text size="sm" tone="muted" className="mt-1">
          Pedido #{order.displayId}
          {status ? ` · ${status}` : ""}
        </Text>

        <Eyebrow size="sm" className="mb-3 mt-6">O que aconteceu?</Eyebrow>
        <SelectableCardGroup
          label="O que aconteceu"
          value={selectedOption}
          onValueChange={setSelectedOption}
          className="flex flex-col gap-2"
        >
          <SelectableCard value={OPTION_CANCEL} title="Quero cancelar o meu pedido" />
        </SelectableCardGroup>

        <Field
          id="problem-reason"
          label="Descreva seu problema"
          hint={`${reason.trim().length}/${MIN_CANCELLATION_REASON_LEN} mínimo · ${reason.length}/500`}
          className="mt-8"
        >
          <TextArea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            maxLength={500}
            placeholder="Conte o que aconteceu (mínimo 10 caracteres)..."
          />
        </Field>
      </PageContainer>

      <FixedFooter>
        <Button
          type="button"
          size="lg"
          fullWidth
          disabled={!canSubmit}
          onClick={() => void handleSubmit()}
        >
          {submitting ? "Enviando..." : "Solicitar cancelamento"}
        </Button>
      </FixedFooter>
    </>
  )
}
